// StudyDost — Profile Page (with editing + parent invite)
// frontend/src/pages/ProfilePage.js

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LogOut, ChevronRight, Edit2, Check, X,
  MessageCircle, Star, Calendar, User,
  BookOpen, Share2, Copy, Users
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import toast from 'react-hot-toast';

const CLASS_OPTIONS = ['1','2','3','4','5','6','7','8','9','10','11','12'];
const BOARD_OPTIONS = [
  { value: 'CBSE',  label: 'CBSE' },
  { value: 'BSEB',  label: 'Bihar Board' },
  { value: 'UP',    label: 'UP Board' },
  { value: 'ICSE',  label: 'ICSE' },
  { value: 'MH',    label: 'Maharashtra Board' },
  { value: 'OTHER', label: 'Other Board' },
];
const LANG_OPTIONS = [
  { value: 'HINDI',    label: 'हिंदी' },
  { value: 'ENGLISH',  label: 'English' },
  { value: 'HINGLISH', label: 'Hinglish' },
];
const PLAN_CONFIG = {
  FREE:            { label: 'Free Plan',      color: '#64748B', bg: '#F1F5F9', icon: '📚' },
  MONTHLY:         { label: 'Monthly Plan',   color: '#4F46E5', bg: '#EEF2FF', icon: '⭐' },
  QUARTERLY:       { label: 'Quarterly Plan', color: '#7C3AED', bg: '#F5F3FF', icon: '💫' },
  ANNUAL:          { label: 'Annual Plan',    color: '#D97706', bg: '#FFFBEB', icon: '👑' },
  PAY_PER_QUESTION:{ label: 'Pay-per-Q',      color: '#D97706', bg: '#FFFBEB', icon: '⚡' },
};

// ── Inline editable field ──────────────────────
function EditableRow({ icon, label, value, onSave, type = 'text', options = null }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');

  const handleSave = () => {
    onSave(draft);
    setEditing(false);
  };
  const handleCancel = () => {
    setDraft(value || '');
    setEditing(false);
  };

  return (
    <div style={styles.row}>
      <div style={styles.rowLeft}>
        <span style={styles.rowIcon}>{icon}</span>
        <span style={styles.rowLabel}>{label}</span>
      </div>
      {editing ? (
        <div style={styles.editRight}>
          {options ? (
            <select
              style={styles.editSelect}
              value={draft}
              onChange={e => setDraft(e.target.value)}
              autoFocus
            >
              {options.map(o => (
                <option key={o.value || o} value={o.value || o}>
                  {o.label || o}
                </option>
              ))}
            </select>
          ) : (
            <input
              style={styles.editInput}
              value={draft}
              onChange={e => setDraft(e.target.value)}
              autoFocus
              onKeyDown={e => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') handleCancel(); }}
            />
          )}
          <button style={styles.saveBtn} onClick={handleSave}>
            <Check size={14} />
          </button>
          <button style={styles.cancelBtn} onClick={handleCancel}>
            <X size={14} />
          </button>
        </div>
      ) : (
        <div style={styles.rowRight}>
          <span style={styles.rowValue}>{value || 'Not set'}</span>
          <button style={styles.editBtn} onClick={() => { setDraft(value || ''); setEditing(true); }}>
            <Edit2 size={13} color="#CBD5E1" />
          </button>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────

const LEVELS = [
  { name: 'Beginner', minXP: 0 },
  { name: 'Explorer',      minXP: 100 },
  { name: 'Scholar',        minXP: 300 },
  { name: 'Expert',       minXP: 700 },
  { name: 'Master',         minXP: 1500 },
];

function getLevelProgress(totalXP) {
  let current = LEVELS[0], next = null;
  for (let i = 0; i < LEVELS.length; i++) {
    if (totalXP >= LEVELS[i].minXP) current = LEVELS[i];
    if (totalXP < LEVELS[i].minXP && !next) next = LEVELS[i];
  }
  const progress = next
    ? Math.round(((totalXP - current.minXP) / (next.minXP - current.minXP)) * 100)
    : 100;
  return { current, next, progress };
}

export default function ProfilePage() {
  const { user, updateUser, logout } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Fetch fresh profile to get referralCode etc. (AuthContext JWT may be stale)
  const { data: freshProfile } = useQuery({
    queryKey: ['myProfile'],
    queryFn: () => api.get('/users/profile').then(r => r.data.user),
    staleTime: 2 * 60 * 1000,
  });
  const profile = freshProfile || user;
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [familyCode, setFamilyCode] = useState(user?.familyCode || null);

  // Sync teacher link status from fresh profile
  React.useEffect(() => {
    if (freshProfile?.referralCode && !teacherLinkStatus?.linked) {
      setTeacherLinkStatus({ linked: true, code: freshProfile.referralCode });
    }
  }, [freshProfile?.referralCode]);
  const [codeCopied, setCodeCopied] = useState(false);
  const [teacherCode, setTeacherCode] = useState('');
  const [teacherLinkStatus, setTeacherLinkStatus] = useState(null);

  const plan = user?.activePlan || user?.plan || 'FREE';
  const planConfig = PLAN_CONFIG[plan] || PLAN_CONFIG.FREE;
  const isPaid = ['MONTHLY', 'QUARTERLY', 'ANNUAL'].includes(plan);
  const expiry = user?.planExpiry;
  const expiryDate = expiry
    ? new Date(expiry).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })
    : null;
  const initials = user?.name
    ? user.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()
    : '??';

  // Update profile mutation
  const updateMutation = useMutation({
    mutationFn: (data) => api.patch('/users/profile', data).then(r => r.data),
    onSuccess: (data) => {
      updateUser(data.user);
      toast.success('Profile updated!');
      queryClient.invalidateQueries(['usage']);
    },
    onError: () => toast.error('Update failed. Try again.'),
  });

  // Generate family code
  const teacherLinkMutation = useMutation({
    mutationFn: (code) => api.post('/users/link-teacher', { code }).then(r => r.data),
    onSuccess: (data) => {
      setTeacherLinkStatus({ linked: true, code: data.referralCode });
      setTeacherCode('');
      toast.success('Teacher linked! 🎓');
    },
    onError: (err) => {
      toast.error(err.response?.data?.error || 'Invalid teacher code');
    },
  });

  const familyCodeMutation = useMutation({
    mutationFn: () => api.post('/users/family-code').then(r => r.data),
    onSuccess: (data) => {
      setFamilyCode(data.familyCode);
      updateUser({ ...user, familyCode: data.familyCode });
    },
    onError: () => toast.error('Failed to generate code'),
  });

  const handleCopyCode = () => {
    navigator.clipboard.writeText(familyCode);
    setCodeCopied(true);
    toast.success('Code copied!');
    setTimeout(() => setCodeCopied(false), 2000);
  };

  const handleShareCode = () => {
    const msg = `Join StudyDost to track my progress!\n\nVisit: studydost.ai/parent\nLogin with your number → enter code: *${familyCode}*`;
    if (navigator.share) {
      navigator.share({ title: 'StudyDost Family Code', text: msg });
    } else {
      navigator.clipboard.writeText(msg);
      toast.success('Share message copied!');
    }
  };

  const avatarGradient = {
    FREE: 'linear-gradient(135deg, #64748B, #94A3B8)',
    MONTHLY: 'linear-gradient(135deg, #4F46E5, #7C3AED)',
    QUARTERLY: 'linear-gradient(135deg, #7C3AED, #A855F7)',
    ANNUAL: 'linear-gradient(135deg, #D97706, #F59E0B)',
  }[plan] || 'linear-gradient(135deg, #4F46E5, #7C3AED)';

  return (
    <div style={styles.page}>

      {/* Hero header */}
      <div style={styles.heroHeader}>
        <div style={styles.heroBg} />
        <div style={styles.heroContent}>
          <div style={{ ...styles.avatar, background: avatarGradient }}>
            {initials}
            {isPaid && <div style={styles.avatarBadge}>{planConfig.icon}</div>}
          </div>
          <h2 style={styles.userName}>{user?.name || 'Student'}</h2>
          <p style={styles.userPhone}>+{user?.phone || ''}</p>
          <div style={styles.planBadge}>
            <span>{planConfig.icon}</span>
            <span>{planConfig.label}</span>
            {expiryDate && <span style={{ opacity: 0.7, fontSize: 11 }}>· Expires {expiryDate}</span>}
          </div>
        </div>
      </div>

      <div style={styles.content}>

        {/* Upgrade card for free users */}
        {!isPaid && (
          <div style={styles.upgradeCard} onClick={() => navigate('/plans')}>
            <div style={styles.upgradeLeft}>
              <div style={styles.upgradeEmoji}>🚀</div>
              <div>
                <div style={styles.upgradeTitle}>Unlimited Questions Unlock Karein</div>
                <div style={styles.upgradeSub}>Diagrams, voice, parent reports — ₹199/month</div>
              </div>
            </div>
            <ChevronRight size={20} color="#4F46E5" />
          </div>
        )}

        {/* ── Profile Details (editable) ── */}
        <div style={styles.section}>
          <div style={styles.sectionTitle}>
            <User size={14} color="#94A3B8" />
            Profile Details
          </div>
          <div style={styles.card}>
            <EditableRow
              icon={<User size={16} color="#4F46E5" />}
              label="Name"
              value={user?.name}
              onSave={v => updateMutation.mutate({ name: v })}
            />
            <div style={styles.divider} />
            <EditableRow
              icon={<BookOpen size={16} color="#4F46E5" />}
              label="Class"
              value={user?.studentClass ? `Class ${user.studentClass}` : null}
              onSave={v => updateMutation.mutate({ studentClass: v.replace('Class ', '') })}
              type="select"
              options={CLASS_OPTIONS.map(c => ({ value: c, label: `Class ${c}` }))}
            />
            <div style={styles.divider} />
            <EditableRow
              icon={<span style={{ fontSize: 16 }}>🎓</span>}
              label="Board"
              value={BOARD_OPTIONS.find(b => b.value === user?.board)?.label || user?.board}
              onSave={v => {
                const found = BOARD_OPTIONS.find(b => b.label === v);
                updateMutation.mutate({ board: found?.value || v });
              }}
              type="select"
              options={BOARD_OPTIONS}
            />
            <div style={styles.divider} />
            <EditableRow
              icon={<span style={{ fontSize: 16 }}>🌐</span>}
              label="Language"
              value={LANG_OPTIONS.find(l => l.value === user?.language)?.label || user?.language}
              onSave={v => {
                const found = LANG_OPTIONS.find(l => l.label === v);
                updateMutation.mutate({ language: found?.value || v });
              }}
              type="select"
              options={LANG_OPTIONS}
            />
            <div style={styles.divider} />
            <EditableRow
              icon={<span style={{ fontSize: 16 }}>🏫</span>}
              label="School"
              value={user?.schoolName}
              onSave={v => updateMutation.mutate({ schoolName: v })}
            />
          </div>
          <p style={styles.editHint}>
            <Edit2 size={11} color="#94A3B8" /> Tap the pencil icon to edit any field
          </p>
        </div>

        {/* ── My Report ── */}
        <button
          style={{width:'100%', background:'linear-gradient(135deg,#1E40AF,#4F46E5)', color:'#FFF', border:'none', borderRadius:16, padding:'14px', fontSize:15, fontWeight:700, cursor:'pointer', marginBottom:12, display:'flex', alignItems:'center', justifyContent:'center', gap:8}}
          onClick={() => navigate('/my-report')}
        >
          📊 My Report
        </button>

        {/* ── Teacher Link ── */}
        <div style={styles.section}>
          <div style={styles.sectionTitle}>
            <span style={{fontSize:14}}>🎓</span>
            Teacher Code
          </div>
          <div style={styles.card}>
            {teacherLinkStatus?.linked ? (
              <div style={{textAlign:'center', padding:'8px 0'}}>
                <div style={{fontSize:28, marginBottom:6}}>✅</div>
                <div style={{fontWeight:700, color:'#059669', marginBottom:4}}>Teacher linked!</div>
                <div style={{fontSize:13, color:'#6B7280'}}>Code: <strong>{teacherLinkStatus.code}</strong></div>
              </div>
            ) : (
              <div>
                <div style={{fontSize:13, color:'#6B7280', marginBottom:12}}>
                  Enter your teacher's referral code to unlock 1 month FREE 🎉
                </div>
                <div style={{display:'flex', gap:8}}>
                  <input
                    style={{flex:1, border:'2px solid #E2E8F0', borderRadius:10, padding:'10px 12px', fontSize:14, outline:'none', fontWeight:600, letterSpacing:1}}
                    placeholder="T-XXXNNN"
                    value={teacherCode}
                    onChange={e => setTeacherCode(e.target.value.toUpperCase())}
                    maxLength={12}
                  />
                  <button
                    style={{background:'#4F46E5', color:'#FFF', border:'none', borderRadius:10, padding:'10px 18px', fontWeight:700, cursor:'pointer', opacity: teacherCode.length > 4 ? 1 : 0.5}}
                    onClick={() => teacherLinkMutation.mutate(teacherCode)}
                    disabled={teacherLinkMutation.isPending || teacherCode.length < 5}
                  >
                    {teacherLinkMutation.isPending ? '...' : 'Link'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── Parent Invite ── */}
        <div style={styles.section}>
          <div style={styles.sectionTitle}>
            <Users size={14} color="#94A3B8" />
            Invite Parents
          </div>
          <div style={styles.card}>
            {!familyCode ? (
              <div style={styles.inviteEmpty}>
                <div style={styles.inviteEmoji}>👨‍👩‍👦</div>
                <div style={styles.inviteTitle}>Parents ko invite karein</div>
                <div style={styles.inviteDesc}>
                  Weekly progress report parents ke WhatsApp par bhejein — bilkul FREE
                </div>
                <button
                  style={styles.generateBtn}
                  onClick={() => familyCodeMutation.mutate()}
                  disabled={familyCodeMutation.isPending}
                >
                  {familyCodeMutation.isPending ? 'Generating...' : '🔗 Family Code Generate Karein'}
                </button>
              </div>
            ) : (
              <div style={styles.familyCodeSection}>
                <div style={styles.familyCodeLabel}>Aapka Family Code</div>
                <div style={styles.familyCodeDisplay}>
                  <span style={styles.familyCode}>{familyCode}</span>
                  <button style={styles.copyCodeBtn} onClick={handleCopyCode}>
                    {codeCopied ? <Check size={16} color="#059669" /> : <Copy size={16} color="#4F46E5" />}
                  </button>
                </div>
                <div style={styles.familyCodeInstructions}>
                  <div style={styles.step}>
                    <span style={styles.stepNum}>1</span>
                    <span>Share this code with your parent</span>
                  </div>
                  <div style={styles.step}>
                    <span style={styles.stepNum}>2</span>
                    <span>Woh <strong>studydost.ai/parent</strong> par jaayein</span>
                  </div>
                  <div style={styles.step}>
                    <span style={styles.stepNum}>3</span>
                    <span>They log in and enter the code</span>
                  </div>
                  <div style={styles.step}>
                    <span style={styles.stepNum}>4</span>
                    <span>They'll receive weekly reports! 📊</span>
                  </div>
                </div>
                <button style={styles.shareCodeBtn} onClick={handleShareCode}>
                  <Share2 size={15} />
                  Share Code with Parents
                </button>
              </div>
            )}
          </div>
        </div>

        {/* ── XP & Streak ── */}
        <div style={styles.section}>
          <div style={styles.sectionTitle}>
            <span style={{fontSize:14}}>⭐</span>
            Progress & Streak
          </div>
          <div style={styles.card}>
            {(() => {
              const totalXP = profile?.totalXP || 0;
              const streak = profile?.streakCount || 0;
              const longest = profile?.longestStreak || 0;
              const { current, next, progress } = getLevelProgress(totalXP);
              const today = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
              const activeToday = profile?.lastStreakDate === today;
              return (
                <div>
                  {/* Level + XP */}
                  <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8}}>
                    <div>
                      <span style={{fontSize:18, fontWeight:800, color:'#4F46E5'}}>{current.name}</span>
                      {next && <span style={{fontSize:12, color:'#94A3B8', marginLeft:8}}>→ {next.name}</span>}
                    </div>
                    <span style={{fontSize:13, fontWeight:700, color:'#7C3AED'}}>{totalXP} XP</span>
                  </div>
                  {/* Progress bar */}
                  <div style={{background:'#E8E6FF', borderRadius:99, height:8, marginBottom:4}}>
                    <div style={{background:'linear-gradient(90deg,#4F46E5,#7C3AED)', borderRadius:99, height:'100%', width:`${progress}%`, transition:'width 0.5s'}} />
                  </div>
                  {next && <div style={{fontSize:11, color:'#94A3B8', marginBottom:14}}>{next.minXP - totalXP} XP to {next.name}</div>}
                  {/* Streak */}
                  <div style={{display:'flex', gap:12, marginTop:8}}>
                    <div style={{flex:1, background:'#FFF7ED', borderRadius:12, padding:'10px 14px', textAlign:'center'}}>
                      <div style={{fontSize:22}}>{streak >= 7 ? '🔥' : streak >= 3 ? '⚡' : '📅'}</div>
                      <div style={{fontSize:20, fontWeight:800, color:'#EA580C'}}>{streak}</div>
                      <div style={{fontSize:11, color:'#9A3412'}}>Day Streak</div>
                      {activeToday && <div style={{fontSize:10, color:'#059669', marginTop:2}}>✅ Aaj active</div>}
                    </div>
                    <div style={{flex:1, background:'#F0FDF4', borderRadius:12, padding:'10px 14px', textAlign:'center'}}>
                      <div style={{fontSize:22}}>🏆</div>
                      <div style={{fontSize:20, fontWeight:800, color:'#16A34A'}}>{longest}</div>
                      <div style={{fontSize:11, color:'#166534'}}>Best Streak</div>
                    </div>
                    <div style={{flex:1, background:'#F5F3FF', borderRadius:12, padding:'10px 14px', textAlign:'center'}}>
                      <div style={{fontSize:22}}>🎖️</div>
                      <div style={{fontSize:20, fontWeight:800, color:'#7C3AED'}}>{(profile?.badges || []).length}</div>
                      <div style={{fontSize:11, color:'#5B21B6'}}>Badges</div>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>

        {/* ── Plan & Billing ── */}
        <div style={styles.section}>
          <div style={styles.sectionTitle}>
            <Star size={14} color="#94A3B8" />
            Plan & Billing
          </div>
          <div style={styles.card}>
            <div style={styles.row}>
              <div style={styles.rowLeft}>
                <span style={styles.rowIcon}>{planConfig.icon}</span>
                <span style={styles.rowLabel}>Current Plan</span>
              </div>
              <div style={{ ...styles.planTag, background: planConfig.bg, color: planConfig.color }}>
                {planConfig.label}
              </div>
            </div>
            {expiryDate && (
              <>
                <div style={styles.divider} />
                <div style={styles.row}>
                  <div style={styles.rowLeft}>
                    <span style={styles.rowIcon}><Calendar size={16} color="#4F46E5" /></span>
                    <span style={styles.rowLabel}>Valid Till</span>
                  </div>
                  <span style={styles.rowValue}>{expiryDate}</span>
                </div>
              </>
            )}
            <div style={styles.divider} />
            <div style={styles.row}>
              <div style={styles.rowLeft}>
                <span style={styles.rowIcon}>📈</span>
                <span style={styles.rowLabel}>{isPaid ? 'Upgrade Plan' : 'Subscribe Now'}</span>
              </div>
              <button style={styles.upgradeSmallBtn} onClick={() => navigate('/plans')}>
                View Plans <ChevronRight size={14} />
              </button>
            </div>
          </div>
        </div>

        {/* ── WhatsApp Bot ── */}
        <div style={styles.section}>
          <div style={styles.sectionTitle}>
            <MessageCircle size={14} color="#94A3B8" />
            WhatsApp Bot
          </div>
          <div style={styles.card}>
            <div style={styles.waRow}>
              <div style={styles.waLeft}>
                <span style={{ fontSize: 28 }}>📱</span>
                <div>
                  <div style={styles.waTitle}>WhatsApp Par Bhi Poochho</div>
                  <div style={styles.waSub}>Kisi bhi waqt, kahin se bhi</div>
                </div>
              </div>
              <a
                href="https://wa.me/15556317048?text=Hi"
                target="_blank"
                rel="noreferrer"
                style={styles.waBtn}
              >
                Open
              </a>
            </div>
          </div>
        </div>

        {/* App info */}
        <div style={styles.appInfo}>
          <div style={{ fontSize: 28 }}>📚</div>
          <div style={styles.appName}>StudyDost</div>
          <div style={styles.appVersion}>v1.0 · Let's Redefine Private Limited</div>
        </div>

        {/* Logout */}
        <button style={styles.logoutBtn} onClick={() => setShowLogoutConfirm(true)}>
          <LogOut size={16} />
          Logout
        </button>
      </div>

      {/* Logout modal */}
      {showLogoutConfirm && (
        <div style={styles.overlay} onClick={() => setShowLogoutConfirm(false)}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 48, marginBottom: 14 }}>👋</div>
            <h3 style={styles.modalTitle}>Logout karna chahte hain?</h3>
            <p style={styles.modalDesc}>Aap dobara login kar sakte hain apne number se.</p>
            <div style={styles.modalBtns}>
              <button style={styles.modalCancel} onClick={() => setShowLogoutConfirm(false)}>
                Cancel
              </button>
              <button style={styles.modalConfirm} onClick={() => { logout(); navigate('/'); }}>
                Logout
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@700;800&family=DM+Sans:wght@400;500;600&display=swap');
      `}</style>
    </div>
  );
}

const styles = {
  page: { height: '100%', overflowY: 'auto', background: '#F8FAFF', fontFamily: "'DM Sans', sans-serif" },

  heroHeader: { position: 'relative', padding: '36px 20px 40px', textAlign: 'center', overflow: 'hidden' },
  heroBg: { position: 'absolute', inset: 0, background: 'linear-gradient(160deg, #0F172A 0%, #1E3A5F 100%)' },
  heroContent: { position: 'relative', zIndex: 1 },
  avatar: { width: 80, height: 80, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, fontWeight: 800, color: '#FFFFFF', margin: '0 auto 14px', position: 'relative', boxShadow: '0 8px 32px rgba(0,0,0,0.3)', fontFamily: "'Sora', sans-serif" },
  avatarBadge: { position: 'absolute', bottom: 0, right: 0, width: 24, height: 24, background: '#FFFFFF', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, boxShadow: '0 2px 8px rgba(0,0,0,0.2)' },
  userName: { fontFamily: "'Sora', sans-serif", fontSize: 22, fontWeight: 800, color: '#FFFFFF', marginBottom: 4 },
  userPhone: { fontSize: 13, color: 'rgba(255,255,255,0.55)', marginBottom: 14 },
  planBadge: { display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.25)', color: '#FFFFFF', borderRadius: 100, padding: '6px 14px', fontSize: 13, fontWeight: 600 },

  content: { padding: '16px 16px 80px' },

  upgradeCard: { background: 'linear-gradient(135deg, #EEF2FF, #F5F3FF)', border: '1.5px solid #C7D2FE', borderRadius: 16, padding: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, cursor: 'pointer' },
  upgradeLeft: { display: 'flex', alignItems: 'center', gap: 12 },
  upgradeEmoji: { fontSize: 32 },
  upgradeTitle: { fontSize: 15, fontWeight: 700, color: '#3730A3', marginBottom: 2 },
  upgradeSub: { fontSize: 12, color: '#6366F1' },

  section: { marginBottom: 20 },
  sectionTitle: { display: 'flex', alignItems: 'center', gap: 7, fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8, paddingLeft: 4 },
  card: { background: '#FFFFFF', borderRadius: 16, border: '1px solid #F1F5F9', overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.04)' },
  divider: { height: 1, background: '#F8FAFF', margin: '0 16px' },
  editHint: { fontSize: 11, color: '#CBD5E1', marginTop: 6, paddingLeft: 4, display: 'flex', alignItems: 'center', gap: 4 },

  // Row styles
  row: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '13px 16px', minHeight: 50 },
  rowLeft: { display: 'flex', alignItems: 'center', gap: 10 },
  rowIcon: { fontSize: 18, width: 24, textAlign: 'center', flexShrink: 0 },
  rowLabel: { fontSize: 14, color: '#374151', fontWeight: 500 },
  rowRight: { display: 'flex', alignItems: 'center', gap: 8 },
  rowValue: { fontSize: 14, color: '#94A3B8', fontWeight: 500 },
  editBtn: { background: 'none', border: 'none', padding: 4, cursor: 'pointer', display: 'flex' },

  // Edit mode
  editRight: { display: 'flex', alignItems: 'center', gap: 6 },
  editInput: { border: '2px solid #4F46E5', borderRadius: 8, padding: '6px 10px', fontSize: 14, outline: 'none', width: 160, fontFamily: "'DM Sans', sans-serif" },
  editSelect: { border: '2px solid #4F46E5', borderRadius: 8, padding: '6px 8px', fontSize: 13, outline: 'none', background: '#FFFFFF', fontFamily: "'DM Sans', sans-serif" },
  saveBtn: { background: '#059669', border: 'none', borderRadius: 8, padding: 7, cursor: 'pointer', color: '#FFFFFF', display: 'flex', alignItems: 'center' },
  cancelBtn: { background: '#F1F5F9', border: 'none', borderRadius: 8, padding: 7, cursor: 'pointer', color: '#64748B', display: 'flex', alignItems: 'center' },

  // Parent invite
  inviteEmpty: { padding: '24px 20px', textAlign: 'center' },
  inviteEmoji: { fontSize: 40, marginBottom: 10 },
  inviteTitle: { fontSize: 15, fontWeight: 700, color: '#0F172A', marginBottom: 6 },
  inviteDesc: { fontSize: 13, color: '#64748B', lineHeight: 1.5, marginBottom: 18 },
  generateBtn: { background: 'linear-gradient(135deg, #4F46E5, #7C3AED)', color: '#FFFFFF', border: 'none', borderRadius: 12, padding: '12px 20px', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" },

  familyCodeSection: { padding: '20px' },
  familyCodeLabel: { fontSize: 12, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 },
  familyCodeDisplay: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 },
  familyCode: { fontFamily: 'monospace', fontSize: 36, fontWeight: 900, color: '#4F46E5', letterSpacing: '0.2em' },
  copyCodeBtn: { background: '#F1F5F9', border: 'none', borderRadius: 10, padding: 10, cursor: 'pointer', display: 'flex' },
  familyCodeInstructions: { display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 },
  step: { display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: '#374151' },
  stepNum: { width: 22, height: 22, borderRadius: '50%', background: '#EEF2FF', color: '#4F46E5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, flexShrink: 0 },
  shareCodeBtn: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%', background: '#4F46E5', color: '#FFFFFF', border: 'none', borderRadius: 12, padding: '12px', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" },

  // Plan
  planTag: { fontSize: 12, fontWeight: 700, padding: '4px 10px', borderRadius: 100 },
  upgradeSmallBtn: { display: 'flex', alignItems: 'center', gap: 4, background: '#EEF2FF', color: '#4F46E5', border: 'none', borderRadius: 100, padding: '6px 12px', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" },

  // WA
  waRow: { padding: '14px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  waLeft: { display: 'flex', alignItems: 'center', gap: 12 },
  waTitle: { fontSize: 14, fontWeight: 700, color: '#0F172A', marginBottom: 2 },
  waSub: { fontSize: 12, color: '#94A3B8' },
  waBtn: { background: '#25D366', color: '#FFFFFF', borderRadius: 100, padding: '8px 16px', fontSize: 13, fontWeight: 700, textDecoration: 'none' },

  // App info
  appInfo: { textAlign: 'center', padding: '20px 0 8px' },
  appName: { fontFamily: "'Sora', sans-serif", fontSize: 16, fontWeight: 800, color: '#0F172A', marginTop: 4 },
  appVersion: { fontSize: 11, color: '#94A3B8', marginTop: 4 },

  logoutBtn: { width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: '#FFF1F2', border: '1px solid #FECDD3', color: '#E11D48', borderRadius: 14, padding: '14px', fontSize: 15, fontWeight: 700, cursor: 'pointer', marginTop: 12, fontFamily: "'DM Sans', sans-serif" },

  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 1000 },
  modal: { background: '#FFFFFF', borderRadius: '24px 24px 0 0', padding: '32px 24px 40px', width: '100%', maxWidth: 480, textAlign: 'center' },
  modalTitle: { fontFamily: "'Sora', sans-serif", fontSize: 20, fontWeight: 800, color: '#0F172A', marginBottom: 8 },
  modalDesc: { fontSize: 14, color: '#64748B', marginBottom: 28, lineHeight: 1.5 },
  modalBtns: { display: 'flex', gap: 12 },
  modalCancel: { flex: 1, background: '#F1F5F9', border: 'none', borderRadius: 12, padding: '14px', fontSize: 15, fontWeight: 700, cursor: 'pointer', color: '#374151', fontFamily: "'DM Sans', sans-serif" },
  modalConfirm: { flex: 1, background: '#E11D48', border: 'none', borderRadius: 12, padding: '14px', fontSize: 15, fontWeight: 700, cursor: 'pointer', color: '#FFFFFF', fontFamily: "'DM Sans', sans-serif" },
};
